/**
 * Phase 286 — Weekly Activity history page.
 *
 * Per-consultant × category × ISO-week grid (the Sales Activity sheet analog).
 * Consumes GET /internal/v0/reporting/weekly-activity via the existing bearer fetcher.
 * Renders the fact-store as-of freshness disclosure (ADR-068 D3 / ADR-074 D4).
 *
 * Wave 8 layout (the 2026-08-17 Sign-Off bounce remediation):
 *   1. the shared <ReportingTabs> strip, so the section is navigable from here;
 *   2. a compact "Weekly totals" summary (consolidated scope) ABOVE
 *   3. the per-consultant detail table, whose Consultant column renders resolved
 *      roster names rather than raw loxo_user_ids.
 * Presentation stays deliberately plain — the BI-grade portal redesign is #1160/#1163.
 *
 * Design tokens only — hex-fence.test.ts POLISH-01.
 */
import * as React from "react";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  EmptyState,
  LastUpdated,
  NavRail,
  PageHeader,
} from "@ardept/design-system";
import { useWeeklyActivity } from "../api/reportingQueries.js";
import { useMe } from "../api/queries.js";
import type { ReportingConsultant, WeeklyActivityCell } from "../api/types.js";
import { ReportingTabs } from "./ReportingTabs.js";
import "./WeeklyActivityScreen.css";
import "./screens.css";

// ── Locked metric vocabulary ────────────────────────────────────────────────
// Mirror of WeeklyReportModel.cs + ReportingReadService.LockedMetricVocabulary.
// LOCKED: do NOT modify without updating WeeklyReportService + ReportingFactStore.
export const LOCKED_METRICS = [
  "cltvs",
  "cints",
  "spec_cvs",
  "submissions",
  "interviews",
  "forwarding_interviews",
  "notes",
  "new_vacancies",
  "new_contacts",
] as const;

export type MetricKey = (typeof LOCKED_METRICS)[number];

/** Short column header labels for the locked metric vocabulary. */
const METRIC_LABELS: Record<MetricKey, string> = {
  cltvs:                 "CLTVs",
  cints:                 "CInts",
  spec_cvs:              "Spec CVs",
  submissions:           "Submissions",
  interviews:            "Interviews",
  forwarding_interviews: "Fwd Ints",
  notes:                 "Notes",
  new_vacancies:         "New Vacs",
  new_contacts:          "New Contacts",
};

/** One display row in the weekly-activity pivot grid. */
export interface GridRow {
  loxoUserId: string;
  isoWeek: string;
  scope: string;
  /** Values keyed by metricKey; zero-initialised for missing cells. */
  values: Record<string, number>;
}

/**
 * Pure transformation: pivots WeeklyActivityCell[] into per-consultant and
 * consolidated display rows, each with values keyed by metricKey.
 *
 * Exported for unit-testability (no React; no fetch; no side effects).
 * The integration test (test/integration/weekly-activity.test.ts) inlines a
 * mirror of this function to verify the locked-vocab contract without importing
 * React types into the node-only integration tsconfig.
 */
export function buildWeeklyGrid(cells: WeeklyActivityCell[]): {
  consultantRows: GridRow[];
  consolidatedRows: GridRow[];
} {
  const lookup = new Map<string, GridRow>();

  for (const cell of cells) {
    const key = `${cell.scope}::${cell.loxo_user_id}::${cell.iso_week}`;
    if (!lookup.has(key)) {
      // Zero-initialise all locked metrics so the grid always has all columns.
      const emptyValues: Record<string, number> = {};
      for (const m of LOCKED_METRICS) emptyValues[m] = 0;
      lookup.set(key, {
        loxoUserId: cell.loxo_user_id,
        isoWeek:    cell.iso_week,
        scope:      cell.scope,
        values:     emptyValues,
      });
    }
    const row = lookup.get(key)!;
    row.values[cell.metric_key] = cell.value;
  }

  const allRows = [...lookup.values()];
  // Consultant rows: sort by loxoUserId asc, then isoWeek desc (latest week first).
  const consultantRows = allRows
    .filter((r) => r.scope === "per_consultant")
    .sort((a, b) =>
      a.loxoUserId.localeCompare(b.loxoUserId) || b.isoWeek.localeCompare(a.isoWeek),
    );
  // Consolidated rows: sort by isoWeek desc (latest week first).
  const consolidatedRows = allRows
    .filter((r) => r.scope === "consolidated")
    .sort((a, b) => b.isoWeek.localeCompare(a.isoWeek));

  return { consultantRows, consolidatedRows };
}

/**
 * Resolves the human name for a grid row's loxo_user_id from the response's
 * `consultants` side table (phase 286 wave 7.1 widened the wire with it).
 *
 * Fallback rule (the phase-334/336 displayName family rule): an id the Loxo roster
 * could not name gets NO entry in `consultants`, and the consumer renders
 * `User {id}` — never a blank, never a fabricated name. A roster entry whose name
 * is blank/whitespace is treated the same as a missing entry, so a degraded roster
 * read can never render an empty Consultant cell.
 *
 * Empty loxoUserId (the consolidated/totals scope) returns "" so the caller can
 * render its own em-dash placeholder.
 *
 * Exported for unit-testability (no React; no fetch; no side effects) — the
 * node-only integration harness mirrors it (test/integration/weekly-activity.test.ts).
 */
export function consultantDisplayName(
  consultants: readonly ReportingConsultant[] | undefined,
  loxoUserId: string,
): string {
  if (!loxoUserId) return "";
  const name = consultants?.find((c) => c.loxo_user_id === loxoUserId)?.name?.trim();
  return name ? name : `User ${loxoUserId}`;
}

// ── Screen component ────────────────────────────────────────────────────────

export function WeeklyActivityScreen(): JSX.Element {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");

  // Auto-select first allowed tenant once /me resolves.
  useEffect(() => {
    if (me?.allowed_tenants.length && !selectedTenantId) {
      setSelectedTenantId(me.allowed_tenants[0]?.id ?? "");
    }
  }, [me, selectedTenantId]);

  const { data, isLoading, error } = useWeeklyActivity({
    tenantId: selectedTenantId,
  });

  const { consultantRows, consolidatedRows } = useMemo(
    () => buildWeeklyGrid(data?.cells ?? []),
    [data],
  );

  // Roster side table: [] whenever the Loxo roster read degraded — a rendering
  // fallback (every cell shows `User {id}`), never an error state.
  const consultants = data?.consultants ?? [];

  const hasRows = consultantRows.length > 0 || consolidatedRows.length > 0;
  const isEmpty = !isLoading && !error && data && data.cells.length === 0;

  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  const tenantOptions = me?.allowed_tenants ?? [];

  return (
    <div className="app">
      <NavRail active="reporting" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Weekly Activity"
          subtitle="Per-consultant sales activity by ISO week. Sourced from the reporting fact store (ADR-074)."
        />

        {/* D-FETCH-01 freshness indicator — sourced from fact-store as_of field */}
        {data?.as_of && <LastUpdated timestamp={new Date(data.as_of)} />}

        {/* Reporting sub-nav — same position ForecastingScreen mounts it (after the
            freshness block) so the strip does not move between the two screens. */}
        <ReportingTabs active="weekly-activity" />

        {/* Tenant selector — shown when operator has access to multiple tenants */}
        {tenantOptions.length > 1 && (
          <div className="wa-filter">
            <label className="wa-filter__label" htmlFor="wa-tenant-select">
              Tenant
            </label>
            <select
              id="wa-tenant-select"
              className="wa-filter__select"
              value={selectedTenantId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelectedTenantId(e.target.value)
              }
            >
              {tenantOptions.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name || t.slug}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Loading state */}
        {isLoading && !data && (
          <div className="wa-loading">Loading weekly activity...</div>
        )}

        {/* Error state */}
        {error && (
          <div className="wa-error" role="status">
            Could not load weekly activity (engine unreachable). Retrying automatically.
          </div>
        )}

        {/* No tenant access */}
        {!isLoading && !error && me && me.allowed_tenants.length === 0 && (
          <EmptyState
            iconName="octagon-alert"
            title="No tenant access"
            body="Ask an administrator to grant you access to a tenant."
          />
        )}

        {/* Empty fact store — no data yet */}
        {isEmpty && !hasRows && (
          <EmptyState
            iconName="bar-chart-2"
            title="No activity data yet"
            body="Weekly sales activity facts will appear here once the first report run completes."
          />
        )}

        {/* Grid — shown when data is loaded and non-empty.
            Two sections, summary FIRST: the agency-wide weekly totals are the
            headline (a handful of rows), the per-consultant breakdown is the
            supporting detail (hundreds of rows). Before wave 8 the totals were
            appended BELOW the detail, which buried them off-screen. Both tables
            render from the same buildWeeklyGrid output — no data reshaping. */}
        {!isLoading && !error && hasRows && (
          <>
            {/* ── Section 1: weekly totals summary (consolidated scope) ── */}
            {consolidatedRows.length > 0 && (
              <section className="wa-section" aria-labelledby="wa-summary-head">
                <h2 className="wa-section__head" id="wa-summary-head">
                  Weekly totals
                </h2>
                <p className="wa-section__note">
                  Agency-wide totals per ISO week, latest week first.
                </p>
                <div className="wa-grid-wrap">
                  <table className="wa-grid" aria-label="Weekly activity totals">
                    <thead>
                      <tr>
                        <th className="wa-th wa-th--week">ISO Week</th>
                        {LOCKED_METRICS.map((m) => (
                          <th key={m} className="wa-th wa-th--metric" title={m}>
                            {METRIC_LABELS[m]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {consolidatedRows.map((row) => (
                        <tr
                          key={`consolidated::${row.isoWeek}`}
                          className="wa-row wa-row--consolidated"
                        >
                          <td className="wa-td wa-td--week">{row.isoWeek}</td>
                          {LOCKED_METRICS.map((m) => (
                            <td
                              key={m}
                              className="wa-td wa-td--metric wa-td--metric-total"
                            >
                              {row.values[m] ?? 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* ── Section 2: per-consultant detail (per_consultant scope) ── */}
            {consultantRows.length > 0 && (
              <section className="wa-section" aria-labelledby="wa-detail-head">
                <h2 className="wa-section__head" id="wa-detail-head">
                  Per-consultant detail
                </h2>
                <p className="wa-section__note">
                  One row per consultant per ISO week, in fixed roster order.
                </p>
                <div className="wa-grid-wrap">
                  <table className="wa-grid" aria-label="Weekly sales activity grid">
                    <thead>
                      <tr>
                        <th className="wa-th wa-th--id">Consultant</th>
                        <th className="wa-th wa-th--week">ISO Week</th>
                        {LOCKED_METRICS.map((m) => (
                          <th key={m} className="wa-th wa-th--metric" title={m}>
                            {METRIC_LABELS[m]}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {consultantRows.map((row) => (
                        <tr
                          key={`${row.loxoUserId}::${row.isoWeek}`}
                          className="wa-row"
                        >
                          {/* Name via the roster side table; the raw loxo_user_id
                              stays reachable as the cell title for support triage. */}
                          <td
                            className="wa-td wa-td--id wa-td--name"
                            title={row.loxoUserId}
                          >
                            {consultantDisplayName(consultants, row.loxoUserId) || (
                              <span className="wa-td__muted">—</span>
                            )}
                          </td>
                          <td className="wa-td wa-td--week">{row.isoWeek}</td>
                          {LOCKED_METRICS.map((m) => (
                            <td key={m} className="wa-td wa-td--metric">
                              {row.values[m] ?? 0}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default WeeklyActivityScreen;
